import { BellIcon, Button, DownloadIcon, MenuButton } from "@strust/ui";
import { css } from "@strust/ui/css";
import { useState } from "react";
import { useIntl } from "react-intl";
import { usePermissions } from "../../../shared/use-permissions.ts";
import { useFormatters } from "../../../shared/use-formatters.ts";
import { ExportsDialog } from "../../exports/components/exports-dialog.tsx";
import { useNotifications } from "../api/notifications.ts";
import { notificationKindMessages, notificationsLabelMessage, shellMessages } from "../messages.ts";

/**
 * The bell of the top bar: the console user's notifications (a background export is ready or
 * failed) and, with `explorer.export`, the shortcut to the exports panel. A failed load is shown
 * as no notifications: the bell never blocks the screen.
 */
const TEXT = css({ m: "0", px: "2.5", py: "1.5", fontSize: "md", color: "text.2" });
const LIST = css({
  display: "flex",
  flexDirection: "column",
  m: "0",
  p: "0",
  listStyle: "none",
  minWidth: "72",
});
const ITEM = css({
  display: "flex",
  flexDirection: "column",
  gap: "0.5",
  px: "2.5",
  py: "2",
  borderRadius: "sm",
  fontSize: "md",
  _hover: { bg: "surface.3" },
});
const TIME = css({ textStyle: "monoSm", color: "text.2" });

export function NotificationsMenu() {
  const intl = useIntl();
  const format = useFormatters();
  const permissions = usePermissions();
  const notifications = useNotifications();
  const [exportsOpen, setExportsOpen] = useState(false);
  const items = notifications.data?.items ?? [];
  const unread = notifications.data?.unread ?? 0;
  const label =
    unread > 0
      ? intl.formatMessage(notificationsLabelMessage, { count: unread })
      : intl.formatMessage(shellMessages.notifications);

  return (
    <>
      <MenuButton label={label} trigger={<BellIcon />}>
        {items.length === 0 ? (
          <p className={TEXT}>{intl.formatMessage(shellMessages.notificationsEmpty)}</p>
        ) : (
          <ul className={LIST}>
            {items.map((item) => (
              <li key={item.id} className={ITEM}>
                <span>{intl.formatMessage(notificationKindMessages[item.kind])}</span>
                <time dateTime={item.created_at} className={TIME}>
                  {format.dateTime(item.created_at)}
                </time>
              </li>
            ))}
          </ul>
        )}
        {permissions.can("explorer.export") ? (
          <Button
            size="sm"
            visual="ghost"
            icon={<DownloadIcon />}
            onClick={() => {
              setExportsOpen(true);
            }}
          >
            {intl.formatMessage(shellMessages.viewExports)}
          </Button>
        ) : null}
      </MenuButton>
      <ExportsDialog
        open={exportsOpen}
        onClose={() => {
          setExportsOpen(false);
        }}
      />
    </>
  );
}
